import moment from 'moment';

/**
 * Calculate and format the age of a cat based on date of birth
 * @param {string} dateOfBirth - Date of birth in YYYY-MM-DD format
 * @param {string} adType - Type of ad ('wanted' or other)
 * @param {array} ages - Array of age ranges (for wanted ads)
 * @returns {string} Formatted age string
 */
export const calculateAge = (dateOfBirth, adType, ages) => {
  if (adType === 'wanted') {
    if (!ages?.length) return 'Any age';

    // Check if all options are selected
    if (ages.length === 5) return 'Any age';

    // Sort ages by their minimum value
    const sortOrder = {
      '2-4-months': 1,
      '5-12-months': 2,
      '1-2-years': 3,
      '3-5-years': 4,
      'older-than-5-years': 5
    };
    const sortedAges = [...ages].sort((a, b) => sortOrder[a] - sortOrder[b]);

    // Check for continuous ranges
    const has24Months = sortedAges.includes('2-4-months');
    const has512Months = sortedAges.includes('5-12-months');
    const has12Years = sortedAges.includes('1-2-years');
    const has35Years = sortedAges.includes('3-5-years');

    // Only combine ranges if they're actually adjacent
    if (has24Months && has512Months && has12Years && has35Years) {
      return '2 months to 5 years';
    }
    if (has512Months && has12Years && has35Years) {
      return '5 months to 5 years';
    }
    if (has24Months && has512Months && has12Years) {
      return '2 months to 2 years';
    }
    if (has512Months && has12Years) {
      return '5 months to 2 years';
    }
    if (has24Months && has512Months) {
      return '2-12 months';
    }
    if (has12Years && has35Years) {
      return '1-5 years';
    }

    // For non-continuous combinations, list them separately
    const ageMap = {
      '2-4-months': '2-4 months',
      '5-12-months': '5-12 months',
      '1-2-years': '1-2 years',
      '3-5-years': '3-5 years',
      'older-than-5-years': 'Older than 5 years'
    };

    return sortedAges.map(age => ageMap[age]).join(', ');
  }

  // For non-wanted ads, calculate age from date of birth
  if (!dateOfBirth) return '-';

  const years = moment().diff(moment(dateOfBirth), 'years');
  const months = moment().diff(moment(dateOfBirth), 'months') % 12;
  const days = moment().diff(moment(dateOfBirth), 'days') % 30;

  const parts = [];
  if (years > 0) parts.push(`${years} ${years === 1 ? 'year' : 'years'}`);
  if (months > 0) parts.push(`${months} ${months === 1 ? 'month' : 'months'}`);
  if (days > 0 && years === 0 && months === 0) parts.push(`${days} ${days === 1 ? 'day' : 'days'}`);

  return parts.length > 0 ? parts.join(', ') : '-';
};

